import * as Sentry from "@sentry/react";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from "react-router";

import type { Route } from "./+types/root";
import { CookieConsent } from "~/components/cookie-consent";
import "./app.css";

export const links: Route.LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=Nunito:wght@400;500;600;700;800;900&display=swap",
  },
];

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        {import.meta.env.PROD && <CookieConsent />}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let title = "Something went wrong";
  let details = "An unexpected error occurred. Please try again or return to the homepage.";
  let statusCode: number | undefined;
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    statusCode = error.status;
    if (error.status === 404) {
      title = "Page not found";
      details = "The page you're looking for doesn't exist or may have been moved.";
    } else {
      details = error.statusText || details;
    }
  } else if (error && error instanceof Error) {
    Sentry.captureException(error);
    if (import.meta.env.DEV) {
      details = error.message;
      stack = error.stack;
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f4f6f9",
        fontFamily: "'Nunito', sans-serif",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          backgroundColor: "#00286f",
          padding: "16px 24px",
          color: "white",
          fontSize: "18px",
          fontWeight: 700,
        }}
      >
        Orchestrate
      </div>
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "48px 32px",
            maxWidth: "480px",
            width: "100%",
            textAlign: "center",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          {statusCode && (
            <div
              style={{
                fontSize: "48px",
                fontWeight: 800,
                color: "#00286f",
                marginBottom: "8px",
              }}
            >
              {statusCode}
            </div>
          )}
          <h1
            style={{
              fontSize: "20px",
              fontWeight: 700,
              color: "#00286f",
              margin: "0 0 8px",
            }}
          >
            {title}
          </h1>
          <p
            style={{
              fontSize: "14px",
              color: "#64748b",
              margin: "0 0 24px",
              lineHeight: 1.5,
            }}
          >
            {details}
          </p>
          <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
            <a
              href="/"
              style={{
                display: "inline-block",
                backgroundColor: "#00286f",
                color: "white",
                padding: "10px 24px",
                borderRadius: "8px",
                textDecoration: "none",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              Go to homepage
            </a>
            {!statusCode && (
              <button
                type="button"
                onClick={() => {
                  const feedback = Sentry.getFeedback();
                  feedback?.createForm().then((form) => {
                    form.appendToDom();
                    form.open();
                  });
                }}
                style={{
                  display: "inline-block",
                  backgroundColor: "white",
                  color: "#00286f",
                  padding: "10px 24px",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: 600,
                  border: "1px solid #00286f",
                  cursor: "pointer",
                }}
              >
                Report this issue
              </button>
            )}
          </div>
          {import.meta.env.DEV && stack && (
            <pre
              style={{
                marginTop: "24px",
                padding: "16px",
                backgroundColor: "#f8fafc",
                borderRadius: "8px",
                fontSize: "12px",
                textAlign: "left",
                overflow: "auto",
                color: "#c62828",
                whiteSpace: "pre-wrap",
              }}
            >
              {stack}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}
